import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { CultureSelectorComponent } from '../../shared/design-system/culture-selector.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { GrowthContentAssetSelectorComponent } from '../../shared/design-system/growth-content-asset-selector.component';
import { GrowthSeoProfileSelectorComponent } from '../../shared/design-system/growth-seo-profile-selector.component';
import { GrowthSegmentSelectorComponent } from '../../shared/design-system/growth-segment-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { KnowledgeSourceSelectorComponent } from '../../shared/design-system/knowledge-source-selector.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { buildGrowthPublishChannelOptions } from './growth-channel.constants';
import {
  GrowthContentAssetEditorState,
  GrowthContentBodyFormat,
  GrowthContentCitationRow,
  GrowthContentPublishTargetRow,
  cloneGrowthContentAssetEditorState,
  onGrowthContentAssetTitleChanged
} from './growth-content-asset-editor.util';

@Component({
  standalone: true,
  selector: 'ain-growth-content-asset-editor',
  imports: [
    AinFormFieldComponent,
    CultureSelectorComponent,
    InfoHintComponent,
    GrowthSegmentSelectorComponent,
    GrowthContentAssetSelectorComponent,
    GrowthSeoProfileSelectorComponent,
    OfferingSelectorComponent,
    KnowledgeSourceSelectorComponent,
    JalaliDateInputComponent,
    FormsModule,
    ButtonModule,
    SelectModule,
    SelectButtonModule,
    InputTextModule,
    TextareaModule,
    EditorModule
  ],
  template: `
    <section class="section-card">
      <div class="section-header">
        <h3>{{ t('growth.contentAsset.sections.basics') }}</h3>
        <ain-info-hint
          [titleKey]="'growth.contentAsset.sections.basics'"
          [descriptionKey]="'growth.contentAsset.help.page.bullets.basics'" />
      </div>

      <div class="grid-2">
        <ain-form-field class="full-width" [label]="t('growth.marketing.campaigns.fields.contentTitle')">
          <input pInputText [(ngModel)]="state.model.title" (ngModelChange)="onTitleChanged()" />
        </ain-form-field>
        <ain-form-field class="full-width" [label]="t('growth.articlePublishing.fields.slug')">
          <input
            pInputText
            [(ngModel)]="state.model.slug"
            (ngModelChange)="onSlugChanged()"
            [placeholder]="t('growth.articlePublishing.fields.slugHint')" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.marketing.campaigns.fields.contentType')">
          <p-select [options]="contentTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="state.model.contentType" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.marketing.campaigns.fields.ownerScope')">
          <p-select [options]="ownerScopeOptions" optionLabel="label" optionValue="value" [(ngModel)]="state.model.ownerScope" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.marketing.campaigns.fields.locale')">
          <ain-culture-selector [(ngModel)]="state.model.locale" [showClear]="false" />
        </ain-form-field>
        <ain-form-field class="full-width" [label]="t('growth.marketing.campaigns.fields.summary')">
          <textarea pInputTextarea rows="3" [(ngModel)]="state.model.summary"></textarea>
        </ain-form-field>
      </div>
    </section>

    <section class="section-card">
      <div class="section-header">
        <h3>{{ t('growth.contentAsset.sections.body') }}</h3>
        <ain-info-hint
          [titleKey]="'growth.contentAsset.sections.body'"
          [descriptionKey]="'growth.contentAsset.help.page.bullets.body'" />
      </div>

      <ain-form-field [label]="t('growth.contentAsset.fields.bodyFormat')">
        <p-selectButton
          [options]="bodyFormatOptions"
          optionLabel="label"
          optionValue="value"
          [(ngModel)]="state.model.bodyFormat" />
      </ain-form-field>

      <ain-form-field class="full-width body-field" [label]="t('growth.contentAsset.fields.body')">
        @if (state.model.bodyFormat === 'markdown') {
          <textarea pInputTextarea rows="14" [(ngModel)]="state.model.bodyMarkdown"></textarea>
        } @else {
          <p-editor [(ngModel)]="state.model.bodyMarkdown" [style]="{ height: '320px' }"></p-editor>
        }
      </ain-form-field>
    </section>

    <section class="section-card">
      <div class="section-header">
        <h3>{{ t('growth.contentAsset.sections.seo') }}</h3>
        <ain-info-hint
          [titleKey]="'growth.contentAsset.help.seo.title'"
          [descriptionKey]="'growth.contentAsset.help.seo.description'" />
      </div>

      <div class="grid-2">
        <ain-form-field class="full-width" [label]="t('growth.contentAsset.fields.metaTitle')">
          <input pInputText [(ngModel)]="state.seo.metaTitle" />
        </ain-form-field>
        <ain-form-field class="full-width" [label]="t('growth.contentAsset.fields.metaDescription')">
          <textarea pInputTextarea rows="3" [(ngModel)]="state.seo.metaDescription"></textarea>
        </ain-form-field>
        <ain-form-field class="full-width" [label]="t('growth.contentAsset.fields.keywords')">
          <textarea pInputTextarea rows="2" [(ngModel)]="state.seo.keywords"></textarea>
        </ain-form-field>
        <ain-form-field [label]="t('growth.contentAsset.fields.titleFa')">
          <input pInputText [(ngModel)]="state.seo.titleFa" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.contentAsset.fields.titleEn')">
          <input pInputText [(ngModel)]="state.seo.titleEn" />
        </ain-form-field>
        <ain-form-field class="full-width" [label]="t('growth.contentAsset.fields.descriptionFa')">
          <textarea pInputTextarea rows="2" [(ngModel)]="state.seo.descriptionFa"></textarea>
        </ain-form-field>
        <ain-form-field class="full-width" [label]="t('growth.contentAsset.fields.descriptionEn')">
          <textarea pInputTextarea rows="2" [(ngModel)]="state.seo.descriptionEn"></textarea>
        </ain-form-field>
        <ain-form-field class="full-width" [label]="t('growth.contentAsset.fields.ogImage')">
          <input pInputText [(ngModel)]="state.seo.ogImage" />
        </ain-form-field>
      </div>
    </section>

    <section class="section-card">
      <div class="section-header">
        <h3>{{ t('growth.contentAsset.sections.citations') }}</h3>
        <ain-info-hint
          [titleKey]="'growth.marketing.campaigns.help.citations.title'"
          [descriptionKey]="'growth.marketing.campaigns.help.citations.description'"
          [bulletKeys]="[
            'growth.marketing.campaigns.help.citations.bullets.sourceType',
            'growth.marketing.campaigns.help.citations.bullets.manual'
          ]" />
      </div>

      @for (row of state.citationRows; track $index) {
        <div class="row-card">
          <div class="row-card-toolbar">
            <span class="row-card-label">{{ t('growth.marketing.campaigns.fields.sourceType') }} #{{ $index + 1 }}</span>
            <button pButton type="button" severity="danger" icon="pi pi-trash" [text]="true" (click)="removeCitation($index)"></button>
          </div>
          <div class="row-card-grid">
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.sourceType')">
              <p-select
                [options]="citationSourceTypeOptions"
                optionLabel="label"
                optionValue="value"
                [ngModel]="row.sourceType"
                (ngModelChange)="onCitationSourceTypeChange(row, $event)"
                appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.sourceRef')">
              @if (row.sourceType === 'offering' || row.sourceType === 'catalog') {
                <ain-offering-selector [(ngModel)]="row.sourceRef" />
              } @else if (row.sourceType === 'segment') {
                <ain-growth-segment-selector [(ngModel)]="row.sourceRef" />
              } @else if (row.sourceType === 'content-asset') {
                <ain-growth-content-asset-selector [(ngModel)]="row.sourceRef" />
              } @else if (row.sourceType === 'knowledge') {
                <ain-knowledge-source-selector [(ngModel)]="row.sourceRef" statusFilter="Active" />
              } @else if (row.sourceType === 'seo-profile') {
                <ain-growth-seo-profile-selector [(ngModel)]="row.sourceRef" />
              } @else {
                <input pInputText [(ngModel)]="row.sourceRef" [placeholder]="t('growth.marketing.campaigns.fields.sourceRefManualPlaceholder')" />
              }
            </ain-form-field>
            <ain-form-field class="full-width" [label]="t('growth.marketing.campaigns.fields.note')">
              <input pInputText [(ngModel)]="row.note" />
            </ain-form-field>
          </div>
        </div>
      }
      <button pButton type="button" severity="secondary" icon="pi pi-plus" [label]="t('common.add')" (click)="addCitation()"></button>
    </section>

    <section class="section-card">
      <div class="section-header">
        <h3>{{ t('growth.contentAsset.sections.publishTargets') }}</h3>
        <ain-info-hint
          [titleKey]="'growth.marketing.campaigns.help.publishTargetRef.title'"
          [descriptionKey]="'growth.marketing.campaigns.help.publishTargetRef.description'" />
      </div>

      @for (row of state.publishTargetRows; track $index) {
        <div class="row-card">
          <div class="row-card-toolbar">
            <span class="row-card-label">{{ t('growth.marketing.campaigns.fields.channel') }} #{{ $index + 1 }}</span>
            <button pButton type="button" severity="danger" icon="pi pi-trash" [text]="true" (click)="removePublishTarget($index)"></button>
          </div>
          <div class="row-card-grid">
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.channel')">
              <p-select [options]="publishChannelOptions" optionLabel="label" optionValue="value" [(ngModel)]="row.channelKey" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.targetRef')">
              <ain-growth-seo-profile-selector [(ngModel)]="row.targetRef" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.scheduleDate')">
              <ain-date-input [(ngModel)]="row.scheduleDateUtc" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.marketing.campaigns.fields.scheduleTime')">
              <input type="time" [(ngModel)]="row.scheduleTimeUtc" />
            </ain-form-field>
          </div>
        </div>
      }
      <button pButton type="button" severity="secondary" icon="pi pi-plus" [label]="t('common.add')" (click)="addPublishTarget()"></button>
    </section>
  `,
  styles: [`
    .section-card {
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      background: #fff;
      padding: 16px;
      margin-bottom: 16px;
      display: grid;
      gap: 16px;
    }

    .section-header {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .section-header h3 {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 600;
    }

    .grid-2 {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .full-width {
      grid-column: 1 / -1;
    }

    .row-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      display: grid;
      gap: 12px;
      background: #fafafa;
    }

    .row-card-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }

    .row-card-label {
      font-weight: 600;
      color: #374151;
    }

    .row-card-grid {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .body-field :host ::ng-deep .p-editor,
    .body-field :host ::ng-deep textarea {
      width: 100%;
    }

    @media (max-width: 768px) {
      .grid-2,
      .row-card-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class GrowthContentAssetEditorComponent {
  private readonly i18n = inject(LocalizationService);

  @Input({ required: true }) state!: GrowthContentAssetEditorState;
  @Output() stateChange = new EventEmitter<GrowthContentAssetEditorState>();

  readonly contentTypeOptions = [
    { value: 'article', label: 'Article' },
    { value: 'landing-page', label: 'Landing page' },
    { value: 'social-post', label: 'Social post' },
    { value: 'brochure', label: 'Brochure' },
    { value: 'pitch-deck', label: 'Pitch deck' },
    { value: 'product-catalog', label: 'Product catalog' },
    { value: 'company-profile', label: 'Company profile' },
    { value: 'video-script', label: 'Video script' },
    { value: 'audio-brief', label: 'Audio brief' },
    { value: 'demo-storyboard', label: 'Demo storyboard' }
  ];

  readonly ownerScopeOptions = [
    { value: 'tenant', label: 'Tenant' },
    { value: 'platform', label: 'Platform' }
  ];

  get publishChannelOptions(): Array<{ value: string; label: string }> {
    return buildGrowthPublishChannelOptions(key => this.t(key));
  }

  get citationSourceTypeOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'knowledge', label: this.t('growth.marketing.campaigns.citationTypes.knowledge') },
      { value: 'catalog', label: this.t('growth.marketing.campaigns.citationTypes.catalog') },
      { value: 'offering', label: this.t('growth.marketing.campaigns.citationTypes.offering') },
      { value: 'content-asset', label: this.t('growth.marketing.campaigns.citationTypes.contentAsset') },
      { value: 'segment', label: this.t('growth.marketing.campaigns.citationTypes.segment') },
      { value: 'seo-profile', label: this.t('growth.marketing.campaigns.citationTypes.seoProfile') },
      { value: 'manual', label: this.t('growth.marketing.campaigns.citationTypes.manual') }
    ];
  }

  get bodyFormatOptions(): Array<{ value: GrowthContentBodyFormat; label: string }> {
    return [
      { value: 'markdown', label: this.t('growth.contentAsset.fields.bodyFormat.markdown') },
      { value: 'html', label: this.t('growth.contentAsset.fields.bodyFormat.html') }
    ];
  }

  onTitleChanged(): void {
    this.emitState(onGrowthContentAssetTitleChanged(this.state));
  }

  onSlugChanged(): void {
    const next = cloneGrowthContentAssetEditorState(this.state);
    next.slugManuallyEdited = true;
    this.emitState(next);
  }

  addCitation(): void {
    const next = cloneGrowthContentAssetEditorState(this.state);
    next.citationRows.push({ sourceType: 'knowledge', sourceRef: '', note: '' });
    this.emitState(next);
  }

  removeCitation(index: number): void {
    const next = cloneGrowthContentAssetEditorState(this.state);
    next.citationRows.splice(index, 1);
    this.emitState(next);
  }

  onCitationSourceTypeChange(row: GrowthContentCitationRow, nextType: string): void {
    if (row.sourceType !== nextType) {
      row.sourceRef = '';
    }
    row.sourceType = nextType;
    this.emitState(cloneGrowthContentAssetEditorState(this.state));
  }

  addPublishTarget(): void {
    const next = cloneGrowthContentAssetEditorState(this.state);
    next.publishTargetRows.push({ channelKey: 'web-blog', targetRef: '', scheduleDateUtc: null, scheduleTimeUtc: '09:00' });
    this.emitState(next);
  }

  removePublishTarget(index: number): void {
    const next = cloneGrowthContentAssetEditorState(this.state);
    next.publishTargetRows.splice(index, 1);
    this.emitState(next);
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private emitState(next: GrowthContentAssetEditorState): void {
    this.state = next;
    this.stateChange.emit(next);
  }
}
